import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CdkTreeModule } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { LottieComponent as NgxLottieComponent } from 'ngx-lottie';
import { ComponentsModule } from '@campus/components';
import { EnvironmentModule } from '@campus/environment';

import { SvgModule } from '@campus/svg';
import { BeDateAdapter, UtilsModule } from '@campus/utils';
import { AbilityMeterComponent } from './ability-meter/ability-meter.component';

import { ParticlesDirective } from './animations';
import { AppBarComponent } from './app-bar/app-bar.component';
import { BannerComponent } from './banner/banner.component';
import {
  BottomNavigationComponent,
  BottomNavigationLogoDirective,
} from './bottom-navigation/bottom-navigation.component';
import { BreadcrumbsTemplateComponent } from './breadcrumbs';
import { BreadcrumbsComponent, BreadcrumbsSeparatorDirective } from './breadcrumbs/breadcrumbs.component';
import { ButtonComponent } from './button/button.component';
import { BorderDirective } from './button/directives/button-border.directive';
import { CircleDirective } from './button/directives/button-circle.directive';
import { DangerDirective } from './button/directives/button-danger.directive';
import { DenseDirective } from './button/directives/button-dense.directive';

import { FabDirective } from './button/directives/button-fab.directive';
import { FlatDirective } from './button/directives/button-flat.directive';
import { IconDirective } from './button/directives/button-icon.directive';
import { InlineDirective } from './button/directives/button-inline.directive';
import { LargeDirective } from './button/directives/button-large.directive';
import { PrimaryDirective } from './button/directives/button-primary.directive';
import { RoundedCornersDirective } from './button/directives/button-rounded-corners.directive';
import { WarningDirective } from './button/directives/button-warning.directive';
import {
  CardActionsDirective,
  CardAvatarDirective,
  CardBadgeDirective,
  CardComponent,
  CardContentDirective,
  CardFooterDirective,
  CardHeaderActionsDirective,
  CardImageDirective,
  CardOverlineDirective,
  CardSelectedIconDirective,
  CardSubTitleDirective,
  CardTitleDirective,
} from './card/card.component';
import { ChartElementComponent } from './charts';
import { ActivityGraphComponent } from './charts/activity-graph/activity-graph.component';
import { BarChartComponent } from './charts/bar-chart/bar-chart.component';
import { GridDiagramComponent } from './charts/grid-diagram/grid-diagram.component';
import { CollapsibleContainerComponent } from './collapsible-container/collapsible-container.component';

import { BrowserModule } from '@campus/browser';
import { dcrIconService } from '@diekeure/dcr-components';
import { BreadcrumbsV2Component } from './breadcrumbs-v2';
import {
  ConceptCardActionDirective,
  ConceptCardComponent,
  ConceptCardImageDirective,
  ConceptCardSubtitleDirective,
  ConceptCardTitleDirective,
} from './concept-card/concept-card.component';
import {
  ConfirmDeleteComponent,
  ConfirmDeleteContentDirective,
  ConfirmDeleteTitleDirective,
} from './confirm-delete/confirm-delete.component';
import { ConfirmableSelectComponent } from './confirmable-select/confirmable-select.component';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { ContentEditableComponent } from './content-editable/content-editable.component';
import { ContentPreviewComponent } from './content-preview/content-preview.component';
import { ContentThumbnailComponent } from './content-thumbnail/content-thumbnail.component';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { DiaboloPhaseComponent } from './diabolo-phase/diabolo-phase.component';
import { DropAreaComponent } from './drop-area/drop-area.component';
import { DropdownMenuItemComponent } from './dropdown-menu-item/dropdown-menu-item.component';
import { DropdownMenuComponent } from './dropdown-menu/dropdown-menu.component';
import { EditableInlineTagListComponent } from './editable-inline-tag-list/editable-inline-tag-list.component';
import { EmptyStateComponent } from './empty-state/empty-state.component';
import { ExpansionPanelContentDirective } from './expansion-panel/expansion-panel-content.directive';
import { ExpansionPanelHeaderIconDirective } from './expansion-panel/expansion-panel-header-icon.directive';
import { ExpansionPanelHeaderDirective } from './expansion-panel/expansion-panel-header.directive';
import { ExpansionPanelComponent } from './expansion-panel/expansion-panel.component';
import { ExpansionPanelsComponent } from './expansion-panel/expansion-panels.component';
import { FileExtensionComponent } from './file-extension/file-extension.component';
import { FileIconComponent } from './file-icon/file-icon.component';
import { FilterTextInputComponent } from './filter-text-input/filter-text-input.component';
import { FolderDetailsDirective } from './folder/directives/folder-details.directive';
import { FolderComponent } from './folder/folder.component';
import { HelpModalComponent } from './help-modal';
import { InfoPanelComponent } from './info-panel/info-panel.component';
import { InputConfirmationModalComponent } from './input-confirmation-modal/input-confirmation-modal.component';
import { LevelCheckboxComponent } from './level-checkbox/level-checkbox.component';
import { LevelIndicatorComponent } from './level-indicator/level-indicator.component';
import {
  ListItemActionsDirective,
  ListItemCaptionDirective,
  ListItemContentComponent,
  ListItemContentDirective,
  ListItemIconDirective,
  ListItemTitleDirective,
} from './list-item-content/list-item-content.component';
import { ListViewComponent, ListViewItemDirective } from './list-view/list-view.component';
import { LottieTriggerDirective } from './lottie/lottie-trigger-for.directive';
import { LottieComponent } from './lottie/lottie.component';
import { ManageCollectionComponent } from './manage-collection/manage-collection.component';
import { CollectionManagerService } from './manage-collection/services/collection-manager.service';
import { COLLECTION_MANAGER_SERVICE_TOKEN } from './manage-collection/services/collection-manager.service.interface';
import { MultiAvatarComponent } from './multi-avatar/multi-avatar.component';
import { MultiCheckBoxTableComponent } from './multi-check-box-table/multi-check-box-table.component';
import {
  MultiCheckBoxTrackByPipe,
  MultiCheckBoxTrackBySubLevelPipe,
} from './multi-check-box-table/multi-check-box-track-by-item.pipe';
import { DropdownDirective } from './notification/directives/notification-dropdown.directive';
import { NotificationComponent } from './notification/notification.component';
import { PageHeaderComponent } from './page-header/page-header.component';
import { PersonBadgeComponent } from './person-badge/person-badge.component';
import { PersonInitialsPipe } from './person-badge/pipes/person-initials.pipe';
import { PersonSummaryComponent } from './person-summary-component/person-summary.component';
import { CompletedProgressIconDirective, ProgressComponent } from './progress/progress.component';
import { RatingComponent } from './rating/rating.component';
import {
  ResultWidgetActionsDirective,
  ResultWidgetComponent,
  ResultWidgetContentDirective,
  ResultWidgetLabelDirective,
} from './result-widget/result-widget.component';
import { ScoreLevelAttemptsComponent, ScoreLevelComponent } from './score-level';
import {
  SectionActionsDirective,
  SectionComponent,
  SectionContentDirective,
  SectionFabContainerDirective,
  SectionFooterActionsDirective,
  SectionTitleDirective,
} from './section/section.component';
import { BackLayerContentTemplateComponent } from './shell/backlayer-content.component';
import { ShellActionsDirective } from './shell/directives/shell-actions';
import { ShellBodyDirective } from './shell/directives/shell-body.directive';
import { ShellBottomDirective } from './shell/directives/shell-bottom.directive';
import { ShellLeftDirective } from './shell/directives/shell-left.directive';
import { ShellLogoDirective } from './shell/directives/shell-logo.directive';
import { ShellTopDirective } from './shell/directives/shell-top.directive';
import { ShellBodyHeaderComponent } from './shell/shell.body-header';
import { ShellComponent } from './shell/shell.component';
import { ShellToolbarComponent } from './shell/shell.toolbar.component';
import { SlideoutModalStackComponent } from './slideout-modal/slideout-modal-stack.component';
import {
  SlideoutModalBottomActionsDirective,
  SlideoutModalComponent,
  SlideoutModalHeaderActionsDirective,
  SlideoutModalHeaderContentDirective,
} from './slideout-modal/slideout-modal.component';
import { SpeedDialComponent } from './speed-dial/speed-dial.component';
import { StickerComponent, StickerImageDirective } from './sticker/sticker.component';
import {
  StrandCardActionDirective,
  StrandCardComponent,
  StrandCardContentDirective,
  StrandCardImageDirective,
  StrandCardStickersDirective,
  StrandCardTitleDirective,
} from './strand-card/strand-card.component';
import { StudentCardActionsDirective, StudentCardComponent } from './student-card/student-card.component';
import { SurveyComponent, SurveyOptionComponent, SurveyQuestionComponent } from './survey';
import { SvgBackgroundComponent } from './svg-background/svg-background.component';
import { TaskProgressBarComponent } from './task-progress-bar/task-progress-bar.component';
import { TermsModalComponent } from './terms-modal/terms-modal.component';
import { TreeNavComponent } from './tree-nav/tree-nav.component';
import { EllipsisTooltipDirective } from './utils/directives/ellipsis-tooltip.directive';
import { HideDesktopDirective } from './utils/directives/hide-desktop.directive';
import { HideMobileDirective } from './utils/directives/hide-mobile.directive';
import { IsMobileDirective } from './utils/directives/is-mobile.directive';
import { ShrinkToDirective, ShrinkToTargetDirective } from './utils/directives/shrink-to.directive';
import { AdjustColorBrightnessPipe } from './utils/pipes/adjust-color-brightness/adjust-color-brightness.pipe';
import { DictionaryHasValuePipe } from './utils/pipes/dictionary/dictionary-has-value.pipe';
import { DictionarySizePipe } from './utils/pipes/dictionary/dictionary-size.pipe';
import { FileSizePipe } from './utils/pipes/file-size/file-size.pipe';
import { HumanDateTimePipe } from './utils/pipes/human-date-time/human-date-time.pipe';
import { JoinPipe } from './utils/pipes/join/join.pipe';
import { RemovePrefixStringPipe } from './utils/pipes/remove-prefix-string/remove-prefix-string.pipe';
import { TimePipe } from './utils/pipes/time/time.pipe';
import { TruncateStringPipe } from './utils/pipes/truncate-string/truncate-string.pipe';
import { WizardProgressComponent } from './wizard-progress/wizard-progress.component';
import { XpCounterComponent, XpCounterIconDirective } from './xp-counter/xp-counter.component';


// leave this for aot build to succeed for elements-ink
// https://angular.io/guide/aot-metadata-errors#expression-form-not-supported
// "You can use typeof and bracket notation in normal application code.
// You just can't use those features within expressions that define Angular metadata."
export interface MappingInterface {
  [key: string]: string;
}

@NgModule({
  imports: [
    OverlayModule,
    EnvironmentModule,
    MatButtonModule,
    CommonModule,
    MatSidenavModule,
    MatInputModule,
    LayoutModule,
    RouterModule,
    CdkTreeModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatMenuModule,
    MatRadioModule,
    MatDatepickerModule,
    MatSliderModule,
    UtilsModule,
    MatDialogModule,
    MatListModule,
    MatTabsModule,
    MatCheckboxModule,
    MatNativeDateModule,
    PortalModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatBadgeModule,
    SvgModule,
    MatChipsModule,
    NgxLottieComponent,
    MatSlideToggleModule,
    ComponentsModule,
    BrowserModule,
  ],
  declarations: [
    FilterTextInputComponent,
    ShellComponent,
    ShellLogoDirective,
    ShellTopDirective,
    ShellActionsDirective,
    ShellLeftDirective,
    ShellBodyDirective,
    ShellBottomDirective,
    AppBarComponent,
    CardComponent,
    StudentCardComponent,
    StudentCardActionsDirective,
    CardAvatarDirective,
    CardTitleDirective,
    CardImageDirective,
    CardSubTitleDirective,
    CardOverlineDirective,
    CardContentDirective,
    CardActionsDirective,
    CardSelectedIconDirective,
    CardFooterDirective,
    CardBadgeDirective,
    CardHeaderActionsDirective,
    PageHeaderComponent,
    FileExtensionComponent,
    TreeNavComponent,
    InfoPanelComponent,
    PersonBadgeComponent,
    PersonInitialsPipe,
    ContentEditableComponent,
    ContentPreviewComponent,
    ConfirmableSelectComponent,
    EditableInlineTagListComponent,
    ContentThumbnailComponent,
    TaskProgressBarComponent,
    ButtonComponent,
    ListViewComponent,
    ListViewItemDirective,
    FolderDetailsDirective,
    FolderComponent,
    AdjustColorBrightnessPipe,
    CircleDirective,
    RoundedCornersDirective,
    BorderDirective,
    DangerDirective,
    WarningDirective,

    BreadcrumbsComponent,
    BreadcrumbsV2Component,
    SlideoutModalBottomActionsDirective,
    BreadcrumbsSeparatorDirective,
    BreadcrumbsTemplateComponent,
    NotificationComponent,
    DropdownMenuComponent,
    TruncateStringPipe,
    DropdownMenuItemComponent,
    HideDesktopDirective,
    HideMobileDirective,
    IsMobileDirective,
    HumanDateTimePipe,
    RemovePrefixStringPipe,
    LargeDirective,
    PrimaryDirective,
    PersonSummaryComponent,
    BannerComponent,
    DropdownDirective,
    DropAreaComponent,
    JoinPipe,
    TimePipe,
    ManageCollectionComponent,
    DictionaryHasValuePipe,
    MultiCheckBoxTableComponent,
    ShellBottomDirective,
    FileIconComponent,
    DateRangePickerComponent,
    ConfirmationModalComponent,
    InputConfirmationModalComponent,
    TermsModalComponent,
    EmptyStateComponent,
    SectionComponent,
    SvgBackgroundComponent,
    InlineDirective,
    DenseDirective,
    FlatDirective,
    FabDirective,
    IconDirective,
    SectionContentDirective,
    SectionActionsDirective,
    SectionFooterActionsDirective,
    SectionTitleDirective,
    SectionFabContainerDirective,
    ListItemContentComponent,
    ListItemIconDirective,
    ListItemTitleDirective,
    ListItemCaptionDirective,
    ListItemActionsDirective,
    ListItemContentDirective,
    BackLayerContentTemplateComponent,
    ProgressComponent,
    CompletedProgressIconDirective,
    CollapsibleContainerComponent,
    RatingComponent,

    ExpansionPanelsComponent,
    ExpansionPanelComponent,
    ExpansionPanelHeaderDirective,
    ExpansionPanelHeaderIconDirective,
    ExpansionPanelContentDirective,
    WizardProgressComponent,
    ResultWidgetComponent,
    ResultWidgetActionsDirective,
    ResultWidgetContentDirective,
    ResultWidgetLabelDirective,
    ChartElementComponent,
    BarChartComponent,
    GridDiagramComponent,
    ActivityGraphComponent,
    DiaboloPhaseComponent,
    FileSizePipe,
    DictionarySizePipe,
    SlideoutModalStackComponent,
    SlideoutModalComponent,
    SlideoutModalHeaderActionsDirective,
    SlideoutModalHeaderContentDirective,
    MultiAvatarComponent,
    ConfirmDeleteComponent,
    ConfirmDeleteTitleDirective,
    ConfirmDeleteContentDirective,
    ConceptCardImageDirective,
    ConceptCardTitleDirective,
    ConceptCardSubtitleDirective,
    ConceptCardActionDirective,
    ConceptCardComponent,
    XpCounterComponent,
    XpCounterIconDirective,
    StrandCardActionDirective,
    StrandCardImageDirective,
    StrandCardStickersDirective,
    StrandCardTitleDirective,
    StrandCardContentDirective,
    StrandCardComponent,
    StickerComponent,
    StickerImageDirective,
    BottomNavigationComponent,
    StrandCardComponent,
    AbilityMeterComponent,
    ParticlesDirective,
    SurveyComponent,
    SurveyOptionComponent,
    SurveyQuestionComponent,
    LevelCheckboxComponent,
    LevelIndicatorComponent,
    SpeedDialComponent,
    EllipsisTooltipDirective,
    ShrinkToDirective,
    ShrinkToTargetDirective,
    LottieComponent,
    LottieTriggerDirective,
    ScoreLevelComponent,
    ScoreLevelAttemptsComponent,
    BottomNavigationLogoDirective,
    ShellToolbarComponent,
    ShellBodyHeaderComponent,
    MultiCheckBoxTrackByPipe,
    MultiCheckBoxTrackBySubLevelPipe,
    HelpModalComponent,
  ],
  exports: [
    ShellToolbarComponent,
    ShellBodyHeaderComponent,
    FilterTextInputComponent,
    LottieComponent,
    LottieTriggerDirective,
    ShellComponent,
    ShellLogoDirective,
    ShellTopDirective,
    ShellActionsDirective,
    ShellLeftDirective,
    ShellBodyDirective,
    ShellBottomDirective,
    AppBarComponent,
    CardComponent,
    StudentCardComponent,
    StudentCardActionsDirective,
    CardAvatarDirective,
    CardTitleDirective,
    CardImageDirective,
    CardSubTitleDirective,
    CardOverlineDirective,
    CardContentDirective,
    CardActionsDirective,
    CardSelectedIconDirective,
    CardFooterDirective,
    CardBadgeDirective,
    CardHeaderActionsDirective,
    PageHeaderComponent,
    InfoPanelComponent,
    ContentEditableComponent,
    ContentPreviewComponent,
    ConfirmableSelectComponent,
    EditableInlineTagListComponent,
    FileExtensionComponent,
    ContentThumbnailComponent,
    ButtonComponent,
    ListViewComponent,
    ListViewItemDirective,
    PersonBadgeComponent,
    FolderDetailsDirective,
    FolderComponent,
    SvgBackgroundComponent,
    AdjustColorBrightnessPipe,
    CircleDirective,
    RoundedCornersDirective,
    BorderDirective,
    DangerDirective,
    WarningDirective,
    TreeNavComponent,
    BreadcrumbsComponent,
    BreadcrumbsV2Component,
    BreadcrumbsSeparatorDirective,
    BreadcrumbsTemplateComponent,
    NotificationComponent,
    DropdownMenuComponent,
    TruncateStringPipe,
    DropdownMenuItemComponent,
    HideDesktopDirective,
    HideMobileDirective,
    IsMobileDirective,
    HumanDateTimePipe,
    RemovePrefixStringPipe,
    LargeDirective,
    PrimaryDirective,
    PersonSummaryComponent,
    BannerComponent,
    DropdownDirective,
    DropAreaComponent,
    JoinPipe,
    DictionaryHasValuePipe,
    DictionarySizePipe,
    FileSizePipe,
    TimePipe,
    MatListModule,
    MatTabsModule,
    MatTooltipModule,
    MultiCheckBoxTableComponent,
    MatCheckboxModule,
    FileIconComponent,
    DateRangePickerComponent,
    ConfirmationModalComponent,
    InputConfirmationModalComponent,
    TermsModalComponent,
    EmptyStateComponent,
    StickerImageDirective,
    StickerComponent,
    SectionComponent,
    InlineDirective,
    DenseDirective,
    FlatDirective,
    FabDirective,
    IconDirective,
    SectionContentDirective,
    SectionActionsDirective,
    SectionFooterActionsDirective,
    SectionTitleDirective,
    SectionFabContainerDirective,
    ListItemContentComponent,
    ListItemIconDirective,
    ListItemTitleDirective,
    ListItemCaptionDirective,
    ListItemActionsDirective,
    ListItemContentDirective,
    BackLayerContentTemplateComponent,
    ProgressComponent,
    CompletedProgressIconDirective,
    CollapsibleContainerComponent,
    RatingComponent,
    ExpansionPanelsComponent,
    ExpansionPanelComponent,
    ExpansionPanelHeaderDirective,
    ExpansionPanelHeaderIconDirective,
    ExpansionPanelContentDirective,
    WizardProgressComponent,
    ResultWidgetComponent,
    ResultWidgetActionsDirective,
    ResultWidgetContentDirective,
    ResultWidgetLabelDirective,
    ChartElementComponent,
    BarChartComponent,
    GridDiagramComponent,
    ActivityGraphComponent,
    GridDiagramComponent,
    DiaboloPhaseComponent,
    SlideoutModalStackComponent,
    SlideoutModalComponent,
    SlideoutModalHeaderActionsDirective,
    SlideoutModalHeaderContentDirective,
    MultiAvatarComponent,
    ConfirmDeleteComponent,
    ConfirmDeleteTitleDirective,
    ConfirmDeleteContentDirective,
    MatFormFieldModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    ConceptCardImageDirective,
    ConceptCardTitleDirective,
    ConceptCardSubtitleDirective,
    ConceptCardActionDirective,
    ConceptCardComponent,
    XpCounterComponent,
    XpCounterIconDirective,
    StrandCardComponent,
    SvgModule,
    StickerComponent,
    StickerImageDirective,
    BottomNavigationComponent,
    StrandCardComponent,
    StrandCardActionDirective,
    StrandCardImageDirective,
    StrandCardStickersDirective,
    StrandCardTitleDirective,
    StrandCardContentDirective,
    ParticlesDirective,
    SurveyComponent,
    AbilityMeterComponent,
    LevelCheckboxComponent,
    LevelIndicatorComponent,
    SpeedDialComponent,
    TaskProgressBarComponent,
    SlideoutModalBottomActionsDirective,
    EllipsisTooltipDirective,
    ShrinkToDirective,
    ShrinkToTargetDirective,
    ScoreLevelComponent,
    ScoreLevelAttemptsComponent,
    BottomNavigationLogoDirective,
    HelpModalComponent,
    CdkTreeModule,
  ],
  providers: [
    {
      provide: COLLECTION_MANAGER_SERVICE_TOKEN,
      useClass: CollectionManagerService,
    },
    { provide: DateAdapter, useClass: BeDateAdapter },
    ManageCollectionComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UiModule {}
